import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Popover, Avatar, SvgIcon, Divider, Chip, Typography } from '@mui/material'
import useUser from 'commons/hooks/useUser'
import { getProfileName } from 'commons/utils/user'
import { LOGOUT_ROUTE } from 'commons/constants/app'
import gtag from 'commons/utils/gtag'
import { useLogoutContext } from './context'
import { Container, LogoutTrigger, StyledIconButton, LogoutLabel, PopoverContent, LogoutButton } from './styles'

function setGtagLogout() {
  gtag('event', 'GA_Event_Menu_Botão_Logout')
}

function LogOut() {
  const { user } = useUser()
  const navigate = useNavigate()
  const { setLogoutComponentActive } = useLogoutContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setLogoutComponentActive(true)
    return () => setLogoutComponentActive(false)
  }, [setLogoutComponentActive])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    setGtagLogout()
    handleClose()
    navigate(LOGOUT_ROUTE)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'user-profile-popover' : undefined

  const initials = user.name
    ? user.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
    : 'U'

  return (
    <Container>
      <LogoutTrigger onClick={handleClick}>
        <StyledIconButton>
          <SvgIcon sx={{ fontSize: 24 }} viewBox="0 -960 960 960">
            <path d="M228.31-164q-27.01 0-45.66-18.65Q164-201.3 164-228.31v-503.38q0-27.01 18.65-45.66Q201.3-796 228.31-796h252.07v52H228.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v503.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h252.07v52H228.31Zm428.92-177.23-37.54-36.39L696.08-454H387.85v-52h308.23l-76.39-76.38 37.54-36.39L796-480 657.23-341.23Z" />
          </SvgIcon>
        </StyledIconButton>
        <LogoutLabel variant="caption">Sair</LogoutLabel>
      </LogoutTrigger>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
            width: '300px',
            marginLeft: '16px',
            overflow: 'hidden',
          },
        }}
      >
        <PopoverContent>
          {/* Avatar */}
          <Avatar
            sx={{
              width: 80,
              height: 80,
              fontSize: '2rem',
              fontWeight: 'bold',
              bgcolor: 'var(--fill-accent-primary-default)',
              color: 'var(--content-inverse)',
              marginBottom: 2,
            }}
          >
            {initials}
          </Avatar>

          {/* User Info */}
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>
            {user.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
            {user.email}
          </Typography>

          {/* Profile Chip */}
          <Chip
            label={getProfileName(user.profiles) || 'Sem perfil'}
            size="small"
            sx={{
              backgroundColor: 'var(--fill-accent-secondary-selected)',
              color: 'var(--content-accent-primary)',
              fontWeight: 600,
              mb: 3,
            }}
          />

          <Divider sx={{ width: '100%', mb: 2 }} />

          {/* Logout Button */}
          <LogoutButton
            variant="contained"
            fullWidth
            onClick={handleLogout}
            startIcon={
              <SvgIcon sx={{ fontSize: 20 }} viewBox="0 -960 960 960">
                <path d="M228.31-164q-27.01 0-45.66-18.65Q164-201.3 164-228.31v-503.38q0-27.01 18.65-45.66Q201.3-796 228.31-796h252.07v52H228.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v503.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h252.07v52H228.31Zm428.92-177.23-37.54-36.39L696.08-454H387.85v-52h308.23l-76.39-76.38 37.54-36.39L796-480 657.23-341.23Z" />
              </SvgIcon>
            }
          >
            Sair da conta
          </LogoutButton>
        </PopoverContent>
      </Popover>
    </Container>
  )
}

export default LogOut
